import { updateGoogleConsent } from "./google-consent";

const COOKIE_TEXT = {
  short:
    "We use cookies to personalise content, provide social media features, analyse traffic, and improve your experience.",
  full: "We use cookies to personalise content and ads, to provide social media features and to analyse our traffic. We also share information about your use of our site with our social media, advertising and analytics partners who may combine it with other information that you've provided to them or that they've collected from your use of their services.",
};

const analyticsCookies = [
  { name: /^_ga(?:_|$)/, path: "/" },
  { name: /^_ga(?:_|$)/, domain: ".modern-expo.eu", path: "/" },
];

function syncGoogleConsent() {
  if (!window.CookieConsent.acceptedCategory("analytics")) {
    analyticsCookies.forEach(({ name, path, domain }) => {
      window.CookieConsent.eraseCookies(name, path, domain);
    });
  }

  const granted = (category) =>
    window.CookieConsent?.acceptedCategory(category) ? "granted" : "denied";

  updateGoogleConsent({
    analytics_storage: granted("analytics"),
    ad_storage: granted("marketing"),
    ad_user_data: granted("marketing"),
    ad_personalization: granted("marketing"),
    functionality_storage: granted("functionality"),
    personalization_storage: granted("functionality"),
  });
}

function getPageLanguage(pathname) {
  if (/^\/ua(\/|$)/.test(pathname)) return "uk";
  if (/^\/pl(\/|$)/.test(pathname)) return "pl";
  return "en";
}

const cookieConsentConfig = {
  onConsent: syncGoogleConsent,
  onChange: syncGoogleConsent,
  guiOptions: {
    consentModal: {
      layout: "bar",
      position: "bottom",
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: "box",
      position: "right",
      equalWeightButtons: true,
      flipButtons: false,
    },
  },
  categories: {
    necessary: { readOnly: true },
    functionality: {},
    analytics: { autoClear: { cookies: analyticsCookies } },
    marketing: {},
  },
  language: {
    default: getPageLanguage(window.location.pathname),
    translations: {
      en: {
        consentModal: {
          title: "This page uses cookies",
          description: COOKIE_TEXT.short,
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          showPreferencesBtn: "Manage preferences",
        },
        preferencesModal: {
          title: "Consent Preferences Center",
          acceptAllBtn: "Accept all",
          acceptNecessaryBtn: "Reject all",
          savePreferencesBtn: "Save preferences",
          closeIconLabel: "Close modal",
          serviceCounterLabel: "Service|Services",
          sections: [
            {
              title: "Cookie Usage",
              description: COOKIE_TEXT.full,
            },
            {
              title: 'Strictly Necessary Cookies <span class="pm__badge">Always Enabled</span>',
              description:
                "Necessary cookies help make a website usable by enabling basic functions like page navigation and access to secure areas of the website. The website cannot function properly without these cookies.",
              linkedCategory: "necessary",
            },
            {
              title: "Functionality Cookies",
              description:
                "Preference cookies enable a website to remember information that changes the way the website behaves or looks, like your preferred language or the region that you are in.",
              linkedCategory: "functionality",
            },
            {
              title: "Analytics Cookies",
              description:
                "Analytics cookies help us understand how visitors interact with the website so we can improve performance and usability.",
              linkedCategory: "analytics",
            },
            {
              title: "Advertisement Cookies",
              description:
                "Marketing cookies are used to personalise content and ads, and to measure the effectiveness of advertising campaigns across platforms.",
              linkedCategory: "marketing",
            },
            {
              title: "More information",
              description:
                'For any queries about our cookie policy, please <a class="cc__link" href="/contact-us">contact us</a>.',
            },
          ],
        },
      },
      uk: {
        consentModal: {
          title: "Цей сайт використовує файли cookie",
          description:
            "Ми використовуємо файли cookie для персоналізації вмісту, надання функцій соціальних мереж, аналізу трафіку та покращення вашого досвіду користування сайтом.",
          acceptAllBtn: "Прийняти всі",
          acceptNecessaryBtn: "Відхилити всі",
          showPreferencesBtn: "Налаштувати",
        },
        preferencesModal: {
          title: "Налаштування згоди на використання cookie",
          acceptAllBtn: "Прийняти всі",
          acceptNecessaryBtn: "Відхилити всі",
          savePreferencesBtn: "Зберегти налаштування",
          closeIconLabel: "Закрити вікно",
          serviceCounterLabel: "Сервіс|Сервіси",
          sections: [
            {
              title: "Використання файлів cookie",
              description:
                "Ми використовуємо файли cookie для персоналізації вмісту та реклами, надання функцій соціальних мереж і аналізу нашого трафіку. Ми також передаємо інформацію про використання вами нашого сайту партнерам із соціальних мереж, реклами й аналітики, які можуть поєднувати її з іншою інформацією, яку ви їм надали або яку вони зібрали під час використання вами їхніх сервісів.",
            },
            {
              title: 'Обов’язкові файли cookie <span class="pm__badge">Завжди ввімкнені</span>',
              description:
                "Обов’язкові файли cookie забезпечують основні функції сайту, як-от навігацію сторінками та доступ до захищених розділів. Без цих файлів cookie сайт не може працювати належним чином.",
              linkedCategory: "necessary",
            },
            {
              title: "Функціональні файли cookie",
              description:
                "Файли cookie налаштувань дають сайту змогу запам’ятовувати інформацію, яка впливає на його роботу чи вигляд, наприклад вибрану вами мову або регіон, у якому ви перебуваєте.",
              linkedCategory: "functionality",
            },
            {
              title: "Аналітичні файли cookie",
              description:
                "Аналітичні файли cookie допомагають нам зрозуміти, як відвідувачі взаємодіють із сайтом, щоб покращувати його продуктивність і зручність користування.",
              linkedCategory: "analytics",
            },
            {
              title: "Рекламні файли cookie",
              description:
                "Маркетингові файли cookie використовуються для персоналізації вмісту та реклами, а також для оцінювання ефективності рекламних кампаній на різних платформах.",
              linkedCategory: "marketing",
            },
            {
              title: "Додаткова інформація",
              description:
                'Якщо у вас є запитання щодо нашої політики використання файлів cookie, будь ласка, <a class="cc__link" href="/ua/contact-us">зв’яжіться з нами</a>.',
            },
          ],
        },
      },
      pl: {
        consentModal: {
          title: "Ta strona używa plików cookie",
          description:
            "Używamy plików cookie, aby personalizować treści, udostępniać funkcje mediów społecznościowych, analizować ruch i poprawiać komfort korzystania ze strony.",
          acceptAllBtn: "Akceptuj wszystkie",
          acceptNecessaryBtn: "Odrzuć wszystkie",
          showPreferencesBtn: "Zarządzaj preferencjami",
        },
        preferencesModal: {
          title: "Centrum preferencji zgód",
          acceptAllBtn: "Akceptuj wszystkie",
          acceptNecessaryBtn: "Odrzuć wszystkie",
          savePreferencesBtn: "Zapisz preferencje",
          closeIconLabel: "Zamknij okno",
          serviceCounterLabel: "Usługa|Usługi",
          sections: [
            {
              title: "Wykorzystanie plików cookie",
              description:
                "Używamy plików cookie, aby personalizować treści i reklamy, udostępniać funkcje mediów społecznościowych oraz analizować nasz ruch. Informacje o korzystaniu z naszej strony przekazujemy także partnerom z obszaru mediów społecznościowych, reklamy i analityki, którzy mogą połączyć je z innymi danymi podanymi przez Ciebie lub zebranymi podczas korzystania z ich usług.",
            },
            {
              title: 'Niezbędne pliki cookie <span class="pm__badge">Zawsze włączone</span>',
              description:
                "Niezbędne pliki cookie zapewniają podstawowe funkcje strony, takie jak nawigacja i dostęp do zabezpieczonych obszarów. Bez nich strona nie może działać prawidłowo.",
              linkedCategory: "necessary",
            },
            {
              title: "Funkcjonalne pliki cookie",
              description:
                "Funkcjonalne pliki cookie pozwalają stronie zapamiętywać informacje, które zmieniają jej działanie lub wygląd, takie jak wybrany język lub region, w którym się znajdujesz.",
              linkedCategory: "functionality",
            },
            {
              title: "Analityczne pliki cookie",
              description:
                "Analityczne pliki cookie pomagają nam zrozumieć, jak odwiedzający korzystają ze strony, abyśmy mogli poprawiać jej wydajność i wygodę użytkowania.",
              linkedCategory: "analytics",
            },
            {
              title: "Reklamowe pliki cookie",
              description:
                "Marketingowe pliki cookie służą do personalizowania treści i reklam oraz do mierzenia skuteczności kampanii reklamowych na różnych platformach.",
              linkedCategory: "marketing",
            },
            {
              title: "Więcej informacji",
              description:
                'W razie pytań dotyczących naszej polityki plików cookie prosimy o <a class="cc__link" href="/pl/contact-us">kontakt</a>.',
            },
          ],
        },
      },
    },
  },
};

const LOAD_EVENTS = ["mousemove", "click", "keydown", "touchstart"];
let hasLoadedCookieConsent = false;
let cookieConsentFallbackTimer = null;
let isCookieConsentReady = false;
let showPreferencesWhenReady = false;

function cleanupCookieConsentLoader() {
  LOAD_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, loadCookieConsent, { capture: true });
  });

  if (cookieConsentFallbackTimer) {
    clearTimeout(cookieConsentFallbackTimer);
  }
}

function loadCookieConsent() {
  if (hasLoadedCookieConsent) return;

  hasLoadedCookieConsent = true;
  cleanupCookieConsentLoader();

  const script = document.createElement("script");
  script.src =
    "https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@3.1.0/dist/cookieconsent.umd.js";
  script.async = true;
  script.onload = () => {
    Promise.resolve(window.CookieConsent.run(cookieConsentConfig)).then(() => {
      isCookieConsentReady = true;

      if (showPreferencesWhenReady) {
        showPreferencesWhenReady = false;
        window.CookieConsent.showPreferences();
      }
    });
  };
  script.onerror = () => {
    console.warn("[CC] Failed to load CookieConsent script");
  };
  document.body.appendChild(script);
}

LOAD_EVENTS.forEach((eventName) => {
  window.addEventListener(eventName, loadCookieConsent, {
    once: true,
    capture: true,
    passive: true,
  });
});

// Any element with [data-cookies-change] reopens the preferences modal.
document.addEventListener("click", (event) => {
  if (!event.target.closest("[data-cookies-change]")) return;

  event.preventDefault();

  if (isCookieConsentReady) {
    window.CookieConsent.showPreferences();
    return;
  }

  // The library is lazy-loaded; open the modal as soon as it is running.
  showPreferencesWhenReady = true;
  loadCookieConsent();
});

cookieConsentFallbackTimer = window.setTimeout(loadCookieConsent, 1000);
